import React, { useContext } from "react";
import { ArrowRight, Lock } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../../ui/dialog";
import { useTranslation } from "../../../hooks/useTranslation";
import { TenantContext } from "../../../contexts/TenantContext";
import { useBillingAccount, useBillingCatalog, useBillingUsage } from "../../../hooks/useApi";
import { BrandStyles, TESTO_AZIONE, VARIABILI_SEZIONE } from "../../integrations/brand";
import { GRIGIO, SCURO } from "../../integrations/casella";
import { CONSIGLIATO, GRATUITO, selfService } from "../../billing/piani";
import { SchedaPiano } from "../../billing/SchedaPiano";

// Le fonti: il bisogno dell'onboarding.
export const FONTI = { limite: "sources_max" };

const prezzoAnno = (p) => p?.price_yearly || (p?.price_monthly || 0) * 12;

/** I dati che servono per decidere quali piani proporre (gia' in cache quasi sempre). */
export function useDatiPiani() {
  // Anche fuori dal provider (es. le pagine nei loro test): senza, nessuno compra.
  const canManageBilling = Boolean(useContext(TenantContext)?.canManageBilling);
  const consumi = useBillingUsage();
  const catalogo = useBillingCatalog();
  const conto = useBillingAccount({ enabled: Boolean(canManageBilling) });
  return {
    canManageBilling,
    consumi: consumi.data,
    catalogo: catalogo.data,
    conto: conto.data,
    // Chi non gestisce l'abbonamento non compra: per lui non serve aspettare.
    pronti: !canManageBilling || Boolean(consumi.data && catalogo.data && conto.data),
  };
}

/**
 * I piani da proporre per un bisogno ({ limite } o { feature }): quelli che si
 * comprano online, piu' cari del piano di adesso, che lo risolvono (piu'
 * fonti, assistenti, domini...; o la funzione compresa), dal piu' economico.
 * Solo a chi puo' pagare (organizzazione principale, titolare) con i
 * pagamenti online attivi; non a chi e' stato venduto offline ne' su un
 * piano su misura. `soloDalGratuito`: la regola dell'onboarding. Se i dati
 * non ci sono, null: resta l'avviso scritto di sempre.
 */
export function propostaPer(bisogno, { canManageBilling, consumi, catalogo, conto }, { soloDalGratuito = false } = {}) {
  const pagamenti = conto?.pagamenti;
  const chiave = consumi?.plan?.key || GRATUITO;
  if (!bisogno || !canManageBilling || (soloDalGratuito && consumi?.plan?.key !== GRATUITO)) return null;
  if (!pagamenti?.stripe_attivo || pagamenti.puo_gestire === false || pagamenti.vendita_offline) return null;
  if (chiave !== GRATUITO && !selfService(chiave)) return null;
  const tutti = catalogo?.plans || [];
  const limiti = consumi?.effective?.limits || {};
  const daBattere = prezzoAnno(tutti.find((p) => p.key === chiave));
  const risolve = (p) =>
    bisogno.feature
      ? Boolean(p.features?.[bisogno.feature])
      : !bisogno.limite || !p.limits?.[bisogno.limite] || p.limits[bisogno.limite] > (limiti[bisogno.limite] || 0);
  const piani = tutti
    .filter((p) => p.key !== GRATUITO && selfService(p.key) && prezzoAnno(p) > daBattere && risolve(p))
    .sort((a, b) => prezzoAnno(a) - prezzoAnno(b));
  return piani.length ? { piani, limiti, bisogno, piano: consumi?.plan || null } : null;
}

/**
 * Il bisogno e' gia' al limite con il piano di adesso? Fonti, assistenti e
 * domini dai consumi; le funzioni dal piano. Le organizzazioni le conta solo
 * il server: per loro decide la sua risposta.
 */
export function alLimite(bisogno, consumi) {
  if (!bisogno || !consumi) return false;
  if (bisogno.feature) return consumi.effective?.features?.[bisogno.feature] === false;
  const massimo = consumi.effective?.limits?.[bisogno.limite] || 0;
  const usati = { sources_max: "sources_count", widgets_max: "widgets_count", domains_max: "domains_count" }[bisogno.limite];
  return massimo > 0 && Boolean(usati) && (consumi.usage?.[usati] || 0) >= massimo;
}

/**
 * I piani da proporre a chi supera le fonti nell'onboarding: solo dal piano
 * gratuito (propostaPer con soloDalGratuito).
 */
export function usePianiDaProporre() {
  const proposta = propostaPer(FONTI, useDatiPiani(), { soloDalGratuito: true });
  // Senza bisogno la finestra usa i testi dell'onboarding (testiDelBisogno).
  return proposta ? { ...proposta, bisogno: null } : null;
}

/**
 * Titolo, testo e bottone sotto. Nell'onboarding (senza bisogno) quelli di
 * sempre; altrimenti per il limite o la funzione, con il nome del piano.
 */
function testiDelBisogno(t, proposta) {
  const bisogno = proposta.bisogno;
  if (!bisogno) {
    return {
      titolo: t("onboardingWizard.plansOverTitle"),
      testo: t("onboardingWizard.plansOverText", { limit: proposta.limiti.sources_max }),
      resta: t("onboardingWizard.continueFree"),
    };
  }
  const chiave = bisogno.feature || bisogno.limite || "generico";
  const piano = !proposta.piano || proposta.piano.key === GRATUITO ? t("billing.plansModal.freePlan") : proposta.piano.name;
  const valori = { plan: piano, count: proposta.limiti?.[bisogno.limite] || 0 };
  return {
    titolo: t(`billing.plansModal.${chiave}.title`, { ...valori, defaultValue: t("billing.plansModal.generico.title", valori) }),
    testo: t(`billing.plansModal.${chiave}.text`, { ...valori, defaultValue: t("billing.plansModal.generico.text", valori) }),
    resta: t("billing.plansModal.notNow"),
  };
}

/**
 * Le fonti non stanno nel piano gratuito: si apre subito la scelta del piano,
 * con le stesse schede della pagina Abbonamento (un solo bottone scuro, quello
 * del piano consigliato). Il gratuito non ha una scheda: e' il bottone grigio
 * sotto, che chiude e lascia continuare l'onboarding. Il pagamento parte dalla
 * pagina Abbonamento, che chiede prima i dati di fatturazione.
 * Nella dashboard (FinestraPiani) la stessa finestra vale per ogni limite:
 * con `proposta.bisogno` il titolo e il testo dicono quale, e il bottone sotto
 * e' «Non ora».
 */
export default function ModalePiani({ proposta, motivo, onScegli, onGratuito }) {
  const { t } = useTranslation();
  const testi = testiDelBisogno(t, proposta);
  // In evidenza il consigliato; se non risolve il bisogno, il primo che lo fa.
  const evidenza = proposta.piani.some((p) => p.key === CONSIGLIATO) ? CONSIGLIATO : proposta.piani[0]?.key;
  return (
    <Dialog open onOpenChange={(aperto) => (aperto ? null : onGratuito())}>
      <DialogContent
        className="max-h-[92vh] max-w-[1000px] overflow-y-auto sm:rounded-2xl"
        // Sfondo leggero: la pagina dietro si intravede (richiesta dopo la live).
        overlayClassName="bg-slate-900/25"
        style={VARIABILI_SEZIONE}
      >
        <BrandStyles />
        <div className="text-center">
          <DialogTitle className="text-2xl font-semibold leading-8 text-[#020617]">{testi.titolo}</DialogTitle>
          <DialogDescription className="mx-auto mt-2 max-w-[640px] text-sm leading-6 text-[#64748B]">
            {motivo || testi.testo}
          </DialogDescription>
        </div>
        {/* Al centro anche con uno o due piani: la griglia e' larga quanto le schede. */}
        <div
          className={`mx-auto mt-4 grid w-full gap-5 ${
            proposta.piani.length === 1
              ? "max-w-[340px]"
              : proposta.piani.length === 2
                ? "max-w-[680px] md:grid-cols-2"
                : "md:grid-cols-3"
          }`}
        >
          {proposta.piani.map((piano) => (
            <SchedaPiano
              key={piano.key}
              piano={piano}
              intervallo="yearly"
              inEvidenza={piano.key === evidenza}
              azione={
                <button
                  type="button"
                  className={`${piano.key === evidenza ? SCURO : GRIGIO} w-full`}
                  onClick={() => onScegli(`/billing?piano=${piano.key}`)}
                >
                  {t("onboardingWizard.planCta", { plan: piano.name })}
                  <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              }
            />
          ))}
        </div>
        <div className="mt-2 flex justify-center">
          <button
            type="button"
            className="inline-flex h-10 items-center justify-center gap-2 rounded-[10px] bg-[#F1F5F9] px-4 text-sm font-semibold text-[#18181B] transition-colors hover:bg-[#E2E8F0] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onClick={onGratuito}
          >
            {testi.resta}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
        <p className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-center text-xs leading-5 text-[#64748B]">
          <Lock className="h-3.5 w-3.5" aria-hidden="true" />
          <span>{t("onboardingWizard.plansTrust")}</span>
          <button type="button" className={`font-semibold ${TESTO_AZIONE}`} onClick={() => onScegli("/billing")}>
            {t("onboardingWizard.plansCompare")}
          </button>
        </p>
      </DialogContent>
    </Dialog>
  );
}
